import React, { useMemo, useState } from "react";
import { Button, Select, InputNumber, Segmented, Switch, Alert, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  ANNUAL_EQUIVALENT,
  DIRECT_DAYS,
  daysForPeriod,
  totalDays,
  validateCoverage,
  defaultRows,
} from "../utils/holidayCalculator";

// HolidayCalculator — UI shell over the pure `holidayCalculator.js` util (Epic 29).
// Props:
//   year       — the year this calculator is editing (display only; state is keyed by it
//                upstream via `key={selectedYear}` so opening a new year reseeds rows).
//   onApply    — (total) => void; called with the live grand total when Apply is clicked.
// All math goes through the util so the web and mobile clients stay in lockstep.
const HolidayCalculator = ({ year, onApply }) => {
  const { t } = useTranslation();

  const [rows, setRows] = useState(() => defaultRows());
  const [round, setRound] = useState(true);

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: t(`holidayCalc.months.${i + 1}`),
      })),
    [t]
  );

  const grandTotal = useMemo(() => totalDays(rows, { round }), [rows, round]);
  const coverage = useMemo(() => validateCoverage(rows), [rows]);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { fromMonth: 1, toMonth: 12, mode: ANNUAL_EQUIVALENT, value: 0 },
    ]);
  };

  const removeRow = (index) => {
    // Never drop below a single row (AC #5).
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const monthNames = (list) => list.map((m) => t(`holidayCalc.months.${m}`)).join(", ");

  return (
    <div style={{ width: 520, maxWidth: "100%" }}>
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        {rows.map((row, index) => (
          <Space
            key={index}
            align="center"
            wrap
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <Space align="center" wrap>
              <Select
                aria-label={t("holidayCalc.fromMonth")}
                value={row.fromMonth}
                onChange={(value) => updateRow(index, { fromMonth: value })}
                options={months}
                style={{ width: 110 }}
              />
              <span>–</span>
              <Select
                aria-label={t("holidayCalc.toMonth")}
                value={row.toMonth}
                onChange={(value) => updateRow(index, { toMonth: value })}
                options={months}
                style={{ width: 110 }}
              />
              <InputNumber
                aria-label={t("holidayCalc.value")}
                min={0}
                value={row.value}
                onChange={(value) => updateRow(index, { value: value ?? 0 })}
                style={{ width: 90 }}
              />
              <Segmented
                value={row.mode}
                onChange={(value) => updateRow(index, { mode: value })}
                options={[
                  { label: t("holidayCalc.annualEquivalent"), value: ANNUAL_EQUIVALENT },
                  { label: t("holidayCalc.directDays"), value: DIRECT_DAYS },
                ]}
              />
            </Space>
            <Space align="center">
              <span style={{ minWidth: 64, textAlign: "right" }}>
                {daysForPeriod(row)} {t("holidayCalc.daysShort")}
              </span>
              <Button
                type="text"
                aria-label={t("holidayCalc.removePeriod")}
                icon={<DeleteOutlined />}
                disabled={rows.length <= 1}
                onClick={() => removeRow(index)}
              />
            </Space>
          </Space>
        ))}

        <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} block>
          {t("holidayCalc.addPeriod")}
        </Button>

        {coverage.gaps.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("holidayCalc.coverageGap", { months: monthNames(coverage.gaps) })}
          />
        )}
        {coverage.overlaps.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={t("holidayCalc.coverageOverlap", {
              months: monthNames(coverage.overlaps),
            })}
          />
        )}
        {coverage.fullCover && (
          <Alert type="success" showIcon message={t("holidayCalc.fullCover")} />
        )}

        <Space style={{ width: "100%", justifyContent: "space-between" }} align="center">
          <Space align="center">
            <Switch checked={round} onChange={setRound} />
            <span>{t("holidayCalc.roundHalfDay")}</span>
          </Space>
          <strong style={{ fontSize: 16 }}>
            {t("holidayCalc.total")}: {grandTotal} {t("holidayCalc.daysShort")}
          </strong>
        </Space>

        <Button type="primary" block onClick={() => onApply(grandTotal)}>
          {t("holidayCalc.apply")}
        </Button>
      </Space>
    </div>
  );
};

export default HolidayCalculator;
